import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, EmptyState, ErrorState, PageHeader, Pill, Skeleton } from "../components/ui";
import { fetchFoodDetail, fetchFoodNutrients } from "../lib/api";
import { trackEvent } from "../lib/analytics";

type AnyObject = Record<string, unknown>;

interface NutrientRow {
  id: string;
  name: string;
  amount: number | null;
  unit: string;
}

interface MacroSnapshot {
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  fiber: number | null;
  sugar: number | null;
  sodium: number | null;
}

function asObject(value: unknown): AnyObject | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as AnyObject;
}

function toStringValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toNumberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function pickFirstString(object: AnyObject | null, keys: string[]): string | null {
  if (!object) {
    return null;
  }
  for (const key of keys) {
    const value = toStringValue(object[key]);
    if (value) {
      return value;
    }
  }
  return null;
}

function pickFirstNumber(object: AnyObject | null, keys: string[]): number | null {
  if (!object) {
    return null;
  }
  for (const key of keys) {
    const value = toNumberValue(object[key]);
    if (value !== null) {
      return value;
    }
  }
  return null;
}

function normalizeNutrientEntry(value: unknown, index: number): NutrientRow | null {
  const row = asObject(value);
  if (!row) {
    return null;
  }
  const nestedNutrient = asObject(row.nutrient);
  const name =
    pickFirstString(row, ["nutrient_name", "nutrientName", "name"]) ??
    pickFirstString(nestedNutrient, ["name", "nutrient_name", "nutrientName"]);
  if (!name) {
    return null;
  }
  const unit =
    pickFirstString(row, ["unit_name", "unitName", "unit"]) ?? pickFirstString(nestedNutrient, ["unitName", "unit_name"]) ?? "";
  const amount = pickFirstNumber(row, ["amount", "value", "quantity"]);
  const id =
    pickFirstString(row, ["id", "nutrient_id", "nutrientId"]) ??
    pickFirstString(nestedNutrient, ["id", "nutrient_id", "number"]) ??
    `${name}-${index}`;
  return { id, name, unit, amount };
}

function extractNutrients(detail: unknown, nutrientsPayload: unknown): NutrientRow[] {
  const rows: NutrientRow[] = [];
  const seen = new Set<string>();
  const roots: unknown[] = [];

  const pushArray = (value: unknown): void => {
    if (!Array.isArray(value)) {
      return;
    }
    roots.push(value);
  };

  pushArray(nutrientsPayload);
  const nutrientsObject = asObject(nutrientsPayload);
  pushArray(nutrientsObject?.items);
  pushArray(nutrientsObject?.nutrients);
  pushArray(nutrientsObject?.foodNutrients);
  pushArray(nutrientsObject?.results);

  const detailObject = asObject(detail);
  pushArray(detailObject?.foodNutrients);
  const detailNutrientsObject = asObject(detailObject?.nutrients);
  pushArray(detailNutrientsObject?.items);
  pushArray(detailNutrientsObject?.foodNutrients);

  for (const list of roots) {
    if (!Array.isArray(list)) {
      continue;
    }
    for (const [index, entry] of list.entries()) {
      const normalized = normalizeNutrientEntry(entry, index);
      if (!normalized) {
        continue;
      }
      const key = `${normalized.name.toLowerCase()}|${normalized.unit.toLowerCase()}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      rows.push(normalized);
    }
  }
  return rows;
}

function extractMacros(detail: unknown, nutrients: NutrientRow[]): MacroSnapshot {
  const detailObject = asObject(detail);
  const labelNutrients = asObject(detailObject?.labelNutrients);
  const directCalories = pickFirstNumber(detailObject, ["calories_kcal", "calories", "kcal"]);
  const directProtein = pickFirstNumber(detailObject, ["protein_g", "protein"]);
  const directCarbs = pickFirstNumber(detailObject, ["carbs_g", "carbohydrate_g", "carbohydrate", "carbs"]);
  const directFat = pickFirstNumber(detailObject, ["fat_g", "total_fat_g", "fat"]);
  const directFiber = pickFirstNumber(detailObject, ["fiber_g", "dietary_fiber_g", "fiber"]);
  const directSugar = pickFirstNumber(detailObject, ["sugar_g", "sugars_g", "sugar"]);
  const directSodium = pickFirstNumber(detailObject, ["sodium_mg", "sodium"]);

  const fromLabel = (keys: string[]): number | null => {
    for (const key of keys) {
      const entry = asObject(labelNutrients?.[key]);
      if (!entry) {
        continue;
      }
      const value = pickFirstNumber(entry, ["value", "amount"]);
      if (value !== null) {
        return value;
      }
    }
    return null;
  };

  const fromNutrients = (matcher: RegExp): number | null => {
    for (const nutrient of nutrients) {
      if (matcher.test(nutrient.name.toLowerCase()) && nutrient.amount !== null) {
        return nutrient.amount;
      }
    }
    return null;
  };

  return {
    calories: directCalories ?? fromLabel(["calories", "energy"]) ?? fromNutrients(/energy|calories|kcal/),
    protein: directProtein ?? fromLabel(["protein"]) ?? fromNutrients(/protein/),
    carbs: directCarbs ?? fromLabel(["carbohydrates", "carbohydrate", "carbs"]) ?? fromNutrients(/carbohydrate|carbs?/),
    fat: directFat ?? fromLabel(["fat", "totalFat"]) ?? fromNutrients(/fat/),
    fiber: directFiber ?? fromLabel(["fiber", "dietaryFiber"]) ?? fromNutrients(/fiber/),
    sugar: directSugar ?? fromLabel(["sugars", "sugar"]) ?? fromNutrients(/sugar/),
    sodium: directSodium ?? fromLabel(["sodium"]) ?? fromNutrients(/sodium/),
  };
}

function servingLabel(detail: unknown): string | null {
  const object = asObject(detail);
  const size = pickFirstNumber(object, ["serving_size", "servingSize"]);
  const unit = pickFirstString(object, ["serving_size_unit", "servingSizeUnit", "unit"]);
  if (size === null || !unit) {
    return null;
  }
  return `${size}${unit}`;
}

function formatAmount(value: number | null, digits = 1): string {
  if (value === null) {
    return "-";
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(digits);
}

export function ExploreFoodDetailPage() {
  const { source = "", fdcId = "" } = useParams();
  const numericId = useMemo(() => Number(fdcId), [fdcId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<unknown>(null);
  const [nutrients, setNutrients] = useState<unknown>(null);
  const normalizedNutrients = useMemo(() => extractNutrients(detail, nutrients), [detail, nutrients]);
  const macros = useMemo(() => extractMacros(detail, normalizedNutrients), [detail, normalizedNutrients]);
  const detailObject = useMemo(() => asObject(detail), [detail]);

  async function load(): Promise<void> {
    if (!source || !numericId || Number.isNaN(numericId)) {
      setError("Invalid food identifier.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [detailPayload, nutrientsPayload] = await Promise.all([
        fetchFoodDetail(source, numericId),
        fetchFoodNutrients(source, numericId),
      ]);
      setDetail(detailPayload);
      setNutrients(nutrientsPayload);
      trackEvent("food_detail_viewed", { source, fdc_id: numericId });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load food detail.");
      setDetail(null);
      setNutrients(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [numericId, source]);

  return (
    <div data-testid="explore-food-detail-page" className="space-y-4 pb-4">
      <PageHeader
        title="Food detail"
        subtitle={`${source}/${fdcId}`}
        right={
          <Link to="/app/explore" className="text-sm font-semibold text-[var(--pr-accent-deep)]">
            Back to explore
          </Link>
        }
      />

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-72" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : (
        <>
          <Card data-testid="food-detail-overview-card">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Food profile</p>
            <h2 className="mt-2 text-xl font-semibold">{pickFirstString(detailObject, ["description"]) || "Food detail unavailable"}</h2>
            <p className="mt-1 text-sm text-[var(--pr-text-muted)]">
              {pickFirstString(detailObject, ["category"]) || "No category"}{" "}
              {pickFirstString(detailObject, ["brand_name", "brandName"]) ? `- ${pickFirstString(detailObject, ["brand_name", "brandName"])}` : ""}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Pill tone="accent" label={source.toUpperCase()} />
              <Pill label={`FDC ${fdcId}`} />
              {servingLabel(detail) ? <Pill label={`Serving ${servingLabel(detail)}`} /> : null}
            </div>
            {pickFirstString(detailObject, ["ingredients"]) ? (
              <p className="mt-3 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2 text-sm text-[var(--pr-text-muted)]">
                {pickFirstString(detailObject, ["ingredients"])}
              </p>
            ) : null}
          </Card>

          <Card>
            <h3 className="text-base font-semibold">Macro snapshot</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Calories</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{formatAmount(macros.calories, 0)}</p>
                <p className="text-xs text-[var(--pr-text-muted)]">kcal</p>
              </Card>
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Protein</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{formatAmount(macros.protein)}</p>
                <p className="text-xs text-[var(--pr-text-muted)]">g</p>
              </Card>
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Carbs</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{formatAmount(macros.carbs)}</p>
                <p className="text-xs text-[var(--pr-text-muted)]">g</p>
              </Card>
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Fat</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{formatAmount(macros.fat)}</p>
                <p className="text-xs text-[var(--pr-text-muted)]">g</p>
              </Card>
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Fiber</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{formatAmount(macros.fiber)}</p>
                <p className="text-xs text-[var(--pr-text-muted)]">g</p>
              </Card>
              <Card flat>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Sugar</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{formatAmount(macros.sugar)}</p>
                <p className="text-xs text-[var(--pr-text-muted)]">g</p>
              </Card>
              <Card flat className="sm:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--pr-text-muted)]">Sodium</p>
                <p className="pr-display mt-2 text-3xl font-semibold">{formatAmount(macros.sodium, 0)}</p>
                <p className="text-xs text-[var(--pr-text-muted)]">mg</p>
              </Card>
            </div>
          </Card>

          <Card>
            <h3 className="text-base font-semibold">Nutrient details</h3>
            {normalizedNutrients.length === 0 ? (
              <EmptyState title="No nutrient rows returned" subtitle="This source did not include nutrient details for the selected item." />
            ) : (
              <div className="mt-3 grid gap-2">
                {normalizedNutrients.map((nutrient) => (
                  <div
                    key={nutrient.id}
                    className="flex items-center justify-between gap-2 rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)] px-3 py-2"
                  >
                    <p className="text-sm font-semibold">{nutrient.name}</p>
                    <p className="text-sm text-[var(--pr-text-muted)]">
                      {nutrient.amount === null ? "-" : formatAmount(nutrient.amount)} {nutrient.unit}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
